import React, { forwardRef } from 'react';
import './Button.css';

/**
 * Button component with ArbiWin design system integration
 * Supports primary, secondary, and danger variants with loading states
 *
 * @param {Object} props - Button properties
 * @param {string} props.variant - Button variant: 'primary', 'secondary', 'danger'
 * @param {string} props.size - Button size: 'small', 'medium', 'large'
 * @param {boolean} props.loading - Show loading spinner
 * @param {boolean} props.disabled - Disable button
 * @param {boolean} props.fullWidth - Make button full width
 * @param {string} props.type - Button type: 'button', 'submit', 'reset'
 * @param {Function} props.onClick - Click handler
 * @param {React.ReactNode} props.children - Button content
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.testId - Test ID for testing
 * @returns {JSX.Element} Button component
 */
const Button = forwardRef(function Button({
  variant = 'primary',
  size = 'medium',
  loading = false,
  disabled = false,
  fullWidth = false,
  type = 'button',
  onClick,
  children,
  className = '',
  testId,
  ...rest
}, ref) {

  const handleClick = function(event) {
    if (loading || disabled) {
      event.preventDefault();
      return;
    }
    if (onClick) {
      onClick(event);
    }
  };

  const buttonClasses = [
    'arbiwin-button',
    'arbiwin-button--' + variant,
    'arbiwin-button--' + size,
    fullWidth ? 'arbiwin-button--full-width' : '',
    loading ? 'arbiwin-button--loading' : '',
    disabled ? 'arbiwin-button--disabled' : '',
    className
  ].filter(Boolean).join(' ');

  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type}
      className={buttonClasses}
      onClick={handleClick}
      disabled={isDisabled}
      data-testid={testId || 'arbiwin-button'}
      aria-disabled={isDisabled}
      aria-busy={loading}
      {...rest}
    >
      {loading && (
        <span className="arbiwin-button__spinner" aria-hidden="true">
          <svg
            className="arbiwin-button__spinner-icon"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="arbiwin-button__spinner-track"
              cx="12"
              cy="12"
              r="10"
              fill="none"
              strokeWidth="2"
            />
            <circle
              className="arbiwin-button__spinner-fill"
              cx="12"
              cy="12"
              r="10"
              fill="none"
              strokeWidth="2"
              strokeDasharray="62.83"
              strokeDashoffset="62.83"
            />
          </svg>
        </span>
      )}
      <span className={loading ? 'arbiwin-button__content--loading' : 'arbiwin-button__content'}>
        {children}
      </span>
    </button>
  );
});

Button.displayName = 'Button';

export default Button;
